import type { ChartResult } from '../domain/types';
import { describeGroundedFact, selectRelevantChartFacts } from './grounding';
import { readingProfileFor } from './readingProfiles';

export type InterpretationSectionId = 'facts' | 'tradition' | 'limits' | 'next' | 'overview';

export interface InterpretationSection {
  id: InterpretationSectionId;
  title: string;
  body: string;
}

export interface ParsedInterpretation {
  lead: string;
  sections: InterpretationSection[];
  plainText: string;
}

export interface InterpretationSummary {
  lead: string;
  points: string[];
}

export interface CoerceInterpretationOptions {
  /** 要求模型至少引用一个真实盘面字段，避免小模型输出百科式套话。 */
  requireChartEvidence?: boolean;
  /** 用于识别小模型把用户问题原样复述成答案的情况。 */
  question?: string;
  /** 追问失败时可传入带上一轮上下文的确定性回退文本。 */
  fallback?: string;
  /** 用于拦截模型把上一轮答案原样再发一次。 */
  previousAnswer?: string;
}

export const MAX_INTERPRETATION_CHARS = 200;

const LOW_CONFIDENCE_MARKERS = [
  /这是一本关于/, /系统整理本/, /神的力量/, /神灵/, /雷神/, /泽雷神/, /本书中没有提及/, /六爻易传的传统解释/,
  /天文学概念/, /地球和水的相互作用/, /地质活动和地质变化/, /实践传统主要涉及/, /用神、六亲、断易、月建日辰等判断框架/,
  /是六爻纳甲的传统解释/, /是六爻纳甲的分歧与限制/, /是六爻纳甲的下一步核验/,
  /是(?:本次|这次)?(?:排盘|盘面)的传统解释/, /是(?:本次|这次)?(?:排盘|盘面)的分歧与限制/,
  /总体来看/, /综合来看/, /保持平常心/, /顺其自然/, /结合实际(?:情况)?/, /先做一小步/
];

/**
 * 小模型偶尔会把卦名、干支等字段误写成神话或书目介绍。
 * 这类文本即使格式完整，也不能直接展示给用户。
 */
export function isLowConfidenceInterpretation(value: string): boolean {
  return LOW_CONFIDENCE_MARKERS.some((marker) => marker.test(value));
}

function naturalFacts(chart: ChartResult, question = ''): string {
  return selectRelevantChartFacts(chart, question, 3)
    .map(describeGroundedFact)
    .join('，');
}

/** 当模型输出明显偏离盘面时，给出基于确定性结果的自然回答。 */
export function buildSafeInterpretationFallback(chart: ChartResult, question = ''): string {
  const cleanedQuestion = question.replace(/\s+/g, ' ').trim();
  const facts = naturalFacts(chart, cleanedQuestion) || `排盘标题“${chart.headline}”`;
  const lens = readingProfileFor(chart.moduleId).lens;
  let answer: string;
  if (!cleanedQuestion) {
    answer = `这次排盘主要显示${facts}。${lens}；没有对应字段的事暂时看不准。`;
  } else if (/什么时候|多久|哪天|何时|时间|月份|日期|应期/.test(cleanedQuestion)) {
    answer = `具体时间暂时看不准。${lens}；目前能确认的是${facts}，还不足以精确到日期。`;
  } else if (/为什么|原因|依据|怎么判断|怎么看出/.test(cleanedQuestion)) {
    answer = `能确认的依据是${facts}。${lens}；没有直接对应的字段就不硬推。`;
  } else if (/重点|主要|说明什么/.test(cleanedQuestion)) {
    answer = `重点是${facts}。${lens}；程序没算出的内容不补写。`;
  } else if (/接下来|下一步|怎么做|做什么/.test(cleanedQuestion)) {
    answer = `当前盘面不能替你确定下一步。${lens}；目前能确认的是${facts}。`;
  } else if (/适合|能不能|可不可以|是否|会不会|行不行/.test(cleanedQuestion)) {
    answer = `这件事从当前盘面暂时看不准。${lens}；能确认的是${facts}，还不足以直接判定能或不能。`;
  } else {
    answer = `这件事从当前盘面暂时看不准。${lens}；当前只能确认${facts}。`;
  }
  return limitInterpretationLength(answer);
}

function includesChartEvidence(value: string, chart: ChartResult): boolean {
  const candidates = [
    chart.headline,
    ...chart.summary.map((fact) => fact.value),
    ...chart.sections.flatMap((section) => section.facts.map((fact) => fact.value))
  ].filter((candidate) => candidate.trim().length >= 2);
  return candidates.some((candidate) => value.includes(candidate));
}

/** 拦截过短、重复或没有实际内容的输出，但不再强迫模型套固定分区。 */
function hasReadableAnswer(value: string): boolean {
  if (value.length > 5_000) return false;
  const readable = directAnswerText(value);
  if (readable.length < 8 || !/[\u4e00-\u9fff]/.test(readable)) return false;
  const lines = readable.split(/\n+/).map((line) => line.trim()).filter((line) => line.length >= 8);
  const normalized = lines.map((line) => line.replace(/[，。！？；：、\s]/g, ''));
  const unique = new Set(normalized);
  return lines.length < 3 || unique.size >= Math.ceil(lines.length / 2);
}

function isQuestionEcho(value: string, question: string): boolean {
  const normalize = (text: string) => text
    .replace(/<[^>]*>/g, '')
    .replace(/[，。！？；：、,.!?;:\s“”'"《》]/g, '')
    .toLocaleLowerCase('zh-CN');
  const answerText = normalize(value);
  const questionText = normalize(question);
  if (questionText.length < 4 || !answerText) return false;
  if (/<\/?(?:user|assistant|system)(?:\s+[^>]*)?>/i.test(value)) return true;
  if (/^\s*(?:问题|用户问题)\s*[:：]/.test(value)) return true;
  return answerText === questionText
    || (answerText.startsWith(questionText) && answerText.length <= questionText.length + 12);
}

function isRepeatedAnswer(value: string, previousAnswer: string): boolean {
  const normalize = (text: string) => directAnswerText(text)
    .replace(/[，。！？；：、,.!?;:\s“”'"《》]/g, '')
    .toLocaleLowerCase('zh-CN');
  const current = normalize(value);
  const previous = normalize(previousAnswer);
  return current.length >= 8 && current === previous;
}

export function coerceInterpretation(value: string, chart: ChartResult, options: CoerceInterpretationOptions = {}): string {
  const trimmed = value.trim();
  const grounded = !options.requireChartEvidence || includesChartEvidence(trimmed, chart);
  const echoed = isQuestionEcho(trimmed, options.question ?? '');
  const repeated = isRepeatedAnswer(trimmed, options.previousAnswer ?? '');
  const accepted = trimmed && !echoed && !repeated && !isLowConfidenceInterpretation(trimmed) && grounded && hasReadableAnswer(trimmed)
    ? directAnswerText(trimmed)
    : options.fallback ?? buildSafeInterpretationFallback(chart, options.question);
  return limitInterpretationLength(accepted);
}

const HEADING_RULES: Array<{ id: InterpretationSectionId; title: string; pattern: RegExp }> = [
  { id: 'facts', title: '你能看到什么', pattern: /盘面事实|事实依据|可计算事实|排盘事实|你能看到什么|你可以看到什么|看到什么/i },
  { id: 'tradition', title: '可能说明什么', pattern: /传统解释|传统理解|古法解读|可能说明什么|可能意味着什么/i },
  { id: 'limits', title: '要注意', pattern: /分歧与限制|分歧|限制|不确定|风险提示|要注意/i },
  { id: 'next', title: '先做什么', pattern: /现实核验|下一步|可核验|行动建议|先做什么|下一步核验/i }
];

function cleanLine(value: string): string {
  return value
    .replace(/^\s*#{1,6}\s*/, '')
    .replace(/^\s*[一二三四五六七八九十\d]+[、.)]\s*/, '')
    .replace(/^\s*[>*-]\s*/, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .trim();
}

function normalizeAnswer(value: string): string {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/^\s*```(?:markdown|text)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^\s*#{1,6}\s*/gm, '')
    .trim();
}

function findHeading(line: string): { id: InterpretationSectionId; title: string; rest: string } | null {
  const cleaned = cleanLine(line);
  if (!cleaned || cleaned.length > 90) return null;

  const leadMatch = cleaned.match(/^(?:一句话结论|先说结论|结论|摘要)\s*[:：-]?\s*(.*)$/i);
  if (leadMatch) return { id: 'overview', title: '一句话结论', rest: leadMatch[1] ?? '' };

  const rule = HEADING_RULES.find((candidate) => {
    if (!candidate.pattern.test(cleaned)) return false;
    return /^(?:盘面事实|事实依据|可计算事实|排盘事实|你能看到什么|你可以看到什么|看到什么|传统解释|传统理解|古法解读|可能说明什么|可能意味着什么|分歧与限制|分歧|限制|不确定|风险提示|要注意|下一步核验|现实核验|可供现实核验的问题|下一步|可核验|行动建议|先做什么)(?:\s*[:：-]|\s*[（(]|\s*$)/i.test(cleaned);
  });
  if (!rule) return null;
  const rest = cleaned
    .replace(/^(?:一|二|三|四|五|六|七|八|九|十|\d+)[、.)]\s*/, '')
    .replace(/^(?:盘面事实|事实依据|可计算事实|排盘事实|你能看到什么|你可以看到什么|看到什么|传统解释|传统理解|古法解读|可能说明什么|可能意味着什么|分歧与限制|分歧|限制|不确定|风险提示|要注意|下一步核验|现实核验|可供现实核验的问题|下一步|可核验|行动建议|先做什么)\s*[:：-]?\s*/i, '')
    .replace(/[（(][^）)]*[）)]/g, '')
    .trim();
  return { id: rule.id, title: rule.title, rest };
}

function firstUsefulSentence(lines: string[]): string {
  const paragraph = lines.join(' ').replace(/\s+/g, ' ').trim();
  if (!paragraph || /^(好的|以下|我将|下面)/.test(paragraph)) return '';
  const sentence = paragraph.match(/^.{1,140}?[。！？!?]/)?.[0] ?? paragraph;
  return sentence.slice(0, 160).trim();
}

export function parseInterpretation(value: string): ParsedInterpretation {
  const plainText = normalizeAnswer(value);
  if (!plainText) return { lead: '', sections: [], plainText: '' };

  const sections: InterpretationSection[] = [];
  const preamble: string[] = [];
  let current: InterpretationSection | null = null;
  let lead = '';

  for (const rawLine of plainText.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const heading = findHeading(line);
    if (heading?.id === 'overview') {
      lead = heading.rest || lead;
      current = null;
      continue;
    }
    if (heading) {
      const previous = sections.at(-1);
      if (previous?.id === heading.id) {
        current = previous;
        if (heading.rest) current.body = `${current.body}${current.body ? '\n' : ''}${heading.rest}`;
      } else {
        current = { id: heading.id, title: heading.title, body: heading.rest };
        sections.push(current);
      }
      continue;
    }
    if (current) {
      current.body = `${current.body}${current.body ? '\n' : ''}${cleanLine(line)}`;
    } else {
      preamble.push(cleanLine(line));
    }
  }

  if (!lead) lead = firstUsefulSentence(preamble);
  if (!sections.length) {
    return { lead: '', sections: [{ id: 'overview', title: '完整解读', body: plainText }], plainText };
  }
  if (preamble.length && !lead) {
    const overview = firstUsefulSentence(preamble);
    if (overview) lead = overview;
  }
  return {
    lead: lead.trim(),
    sections: sections.filter((section) => section.body.trim()).map((section) => ({ ...section, body: section.body.trim() })),
    plainText
  };
}

const REDUNDANT_DISCLAIMER = /^(?:本内容由\s*AI\s*生成|AI\s*生成内容|仅供传统文化研究与娱乐)/i;

/**
 * 把新旧模型输出都整理成普通自然段。旧式五段标签只用于兼容，不再展示给用户。
 */
export function directAnswerText(value: string): string {
  const parsed = parseInterpretation(value);
  if (!parsed.plainText) return '';

  const pieces: string[] = [];
  if (parsed.lead) pieces.push(parsed.lead);
  for (const section of parsed.sections) {
    if (section.body.trim()) pieces.push(section.body);
  }
  if (!pieces.length) pieces.push(parsed.plainText);

  const seen = new Set<string>();
  return pieces
    .flatMap((piece) => piece.split(/\n+/))
    .map((line) => cleanLine(line).replace(/^(?:答|回答)\s*[:：]\s*/, '').trim())
    .filter((line) => line && !REDUNDANT_DISCLAIMER.test(line))
    .filter((line) => {
      if (seen.has(line)) return false;
      seen.add(line);
      return true;
    })
    .join('\n\n');
}

/** 所有提供方统一执行 200 字上限，优先保留完整句子。 */
export function limitInterpretationLength(value: string, maxChars = MAX_INTERPRETATION_CHARS): string {
  const cleaned = value.trim();
  const sentences = cleaned.match(/[^。！？!?；;\n]+[。！？!?；;]?/g)?.map((sentence) => sentence.trim()).filter(Boolean) ?? [];
  const seen = new Set<string>();
  const uniqueSentences = sentences.filter((sentence) => {
    const key = sentence.replace(/[，。！？；：、,.!?;:\s]/g, '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const deduplicated = uniqueSentences.join('') || cleaned;
  if (deduplicated.length <= maxChars) return deduplicated;
  let result = '';
  for (const sentence of uniqueSentences) {
    if (result.length + sentence.length > maxChars) break;
    result += sentence;
  }
  if (result) return result;
  return `${deduplicated.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

function summarySentence(value: string): string {
  const cleaned = value
    .split('\n')
    .map(cleanLine)
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return firstUsefulSentence([cleaned]);
}

/** 提取首屏摘要，避免把模型的完整分区直接堆给用户。 */
export function summarizeInterpretation(value: string): InterpretationSummary {
  const parsed = parseInterpretation(value);
  const hasLegacySections = parsed.sections.some((section) => section.id !== 'overview');
  if (!hasLegacySections) {
    const sentences = directAnswerText(value)
      .split(/(?<=[。！？!?])\s*|\n+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);
    return { lead: sentences[0] ?? '', points: sentences.slice(1, 4) };
  }
  const points: string[] = [];
  const seen = new Set<string>();
  const sectionPriority: Record<InterpretationSectionId, number> = { facts: 1, tradition: 2, next: 3, limits: 4, overview: 5 };
  const orderedSections = [...parsed.sections].sort((left, right) => sectionPriority[left.id] - sectionPriority[right.id]);
  for (const section of orderedSections) {
    const sentence = summarySentence(section.body);
    if (!sentence) continue;
    const point = `${section.title}：${sentence}`;
    if (!seen.has(point)) {
      seen.add(point);
      points.push(point);
    }
    if (points.length >= 3) break;
  }
  if (!points.length && parsed.lead) points.push(parsed.lead);
  return {
    lead: parsed.lead || points.shift() || '',
    points: points.slice(0, 3)
  };
}

/** 小模型无法承接追问时，只按当前追问和真实盘面字段回答。 */
export function buildSafeFollowUpFallback(chart: ChartResult, question: string): string {
  const facts = naturalFacts(chart, question) || `排盘标题“${chart.headline}”`;
  if (/为什么|依据|怎么判断|怎么看出/.test(question)) {
    return limitInterpretationLength(`能确认的依据是${facts}。这些字段来自当前排盘；没有对应当前问题的计算字段，就不能继续补写结论。`);
  }
  if (/什么时候|多久|哪天|时间/.test(question)) {
    return limitInterpretationLength(`具体时间看不准。当前只能确认${facts}；没有明确应期（时间线索），不能精确到哪天。`);
  }
  if (/接下来|下一步|怎么做|做什么/.test(question)) {
    return limitInterpretationLength(`当前盘面不能替你确定下一步。能确认的是${facts}；行动仍要结合现实条件。`);
  }
  return buildSafeInterpretationFallback(chart, question);
}
